import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { InputMaybe, SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import { FormErrorMessage, FormItem, FormLabel } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";
import { DatePickerRange } from "@/components/ui/input/date-picker-range";

type Props = {
	isEdited: boolean;
	hasError: boolean;

	isLimitedSale: boolean;

	saleTypeForTerm: ForNodeType<SaleTypeForTerm>;
	saleTypeForTermOptions: FormOptionType[];
	onSetSaleTypeForTerm: (input: string) => void;

	saleStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetSaleTerm: (input: DateRange) => void;

	stayableStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetStayableTerm: (input: DateRange) => void;

	guide?: string;
	title?: string;
};

export const QuestionSalesTerm = memo(
	({
		isEdited,
		hasError,

		isLimitedSale,

		saleTypeForTerm,
		saleTypeForTermOptions,
		onSetSaleTypeForTerm,

		saleStart,
		saleEnd,
		onSetSaleTerm,

		stayableStart,
		stayableEnd,
		onSetStayableTerm,

		title: _title,
		guide: _guide,
	}: Props) => {
		const title = _title || "プランの販売期間";
		const guide =
			_guide ||
			"プランの販売期間を設定することができます。期間限定を選択した場合、公開期間中に予約された宿泊可能期間に滞在する予約でのみ、プランが購入可能になります。<br />この設定は在庫管理から上書き可能です。";

		return (
			<div className={cn(isLimitedSale && "space-y-6")}>
				<FeatureIslandCategoryQuestionForMain
					required={false}
					title={title}
					guide={guide}
					isEdited={isEdited}
					hasError={hasError}
					inputChildren={
						<RadioGroupField
							value={saleTypeForTerm.value}
							options={saleTypeForTermOptions}
							handleValueChange={onSetSaleTypeForTerm}
						/>
					}
				/>

				<div className={cn(!isLimitedSale && "visually-hidden")}>
					<FeatureIslandCategoryQuestionForMain
						required={false}
						title={""}
						hasError={hasError}
						isEdited={isEdited}
						inputChildren={
							<div className="space-y-10 pb-4">
								<div className="flex flex-col gap-y-1">
									<FormItem>
										<FormLabel>公開する期間</FormLabel>
										<DatePickerRange
											selected={{
												from: saleStart?.value ?? undefined,
												to: saleEnd?.value ?? undefined,
											}}
											onSelect={onSetSaleTerm}
										/>
										<FormErrorMessage>{saleStart?.error || saleEnd?.error}</FormErrorMessage>
									</FormItem>
								</div>
								<div className="flex flex-col gap-y-1">
									<FormItem>
										<FormLabel>滞在可能な期間</FormLabel>
										<DatePickerRange
											selected={{
												from: stayableStart?.value ?? undefined,
												to: stayableEnd?.value ?? undefined,
											}}
											onSelect={onSetStayableTerm}
										/>
										<FormErrorMessage>
											{stayableStart?.error || stayableEnd?.error}
										</FormErrorMessage>
									</FormItem>
								</div>
							</div>
						}
					/>
				</div>
			</div>
		);
	},
);

QuestionSalesTerm.displayName = "QuestionSalesTerm";
